<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import AppButton from '@uiKit/elements/Button.vue'

// One file on a row: thumb · name + meta · Preview · optional remove.
// The single look for every picked or already-uploaded file, so an upload
// field and a documents list read the same. Content (meta, action label)
// is the caller's.

interface Props {
  name: string
  /** "PDF · 2.4 MB · Just now" / "Emirates ID · 12 Mar 2026". */
  meta?: string
  icon?: PiIconName
  actionLabel?: string
  /** Show the ✕ that removes the file. */
  removable?: boolean
  disabled?: boolean
}

withDefaults(defineProps<Props>(), {
  meta: '',
  icon: 'file',
  actionLabel: 'Preview',
  removable: false,
  disabled: false,
})

const emit = defineEmits<{ (e: 'preview' | 'remove'): void }>()
</script>

<template>
  <Box align="center" gap="3" class="file-row">
    <Box align="center" justify="center" class="file-row__thumb" aria-hidden="true">
      <PiIcon :name="icon" class="file-row__thumb-icon" />
    </Box>

    <Box direction="col" gap="1" grow class="file-row__info">
      <Text is="span" type="heading" weight="semibold" truncate :title="name">{{ name }}</Text>
      <Text v-if="meta" is="span" type="body" color="muted" truncate>{{ meta }}</Text>
    </Box>

    <Box align="center" gap="2" class="file-row__actions">
      <AppButton
        :label="actionLabel"
        variant="outline"
        color="secondary"
        icon="eye"
        :aria-label="`${actionLabel} ${name}`"
        :disabled="disabled"
        class="file-row__action"
        @click="emit('preview')"
      />
      <AppButton
        v-if="removable"
        variant="outline"
        color="secondary"
        icon="times"
        aria-label="Remove file"
        :disabled="disabled"
        class="file-row__remove"
        @click="emit('remove')"
      />
    </Box>
  </Box>
</template>

<style scoped>
.file-row {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
  box-sizing: border-box;
}

.file-row__thumb {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: var(--p-border-radius-md);
  border: 1px solid var(--p-primary-100);
  background: var(--p-primary-50);
  box-sizing: border-box;
}

.file-row__thumb-icon {
  font-size: 1.375rem;
  color: var(--p-primary-600);
}

/* min-width:0 lets a long name ellipsise instead of pushing the buttons out. */
.file-row__info {
  min-width: 0;
}

.file-row__actions {
  flex-shrink: 0;
}

.file-row__action,
.file-row__remove {
  min-height: 44px;
}

.file-row__remove {
  width: 44px;
  padding: 0;
}

/* Phones: the eye alone keeps the name readable. */
@media (max-width: 640px) {
  .file-row__action :deep(.p-button-label) {
    display: none;
  }

  .file-row__action {
    width: 44px;
    padding: 0;
  }
}
</style>
